// Where the signed-in person is signed in, and "Sign out everywhere" (Session ASB, Item 3); rendered by MyAccountPage.
//
// Each row is one live server-side session: a short device summary ("Chrome on Windows"), when it signed in, when it
// was last active and whether it is this browser. The server never sends an id, the address or the raw browser string,
// so none can be shown. "Sign out everywhere" ends every one of them, this browser included, through the branded
// two-step confirm, and then returns to the sign-in page. The office shell is office and admin only, so this card is too.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import type { SignInSessionItem } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { clearNavExpanded } from "../../lib/navExpanded";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Loader } from "../../components/Loader";
import { formatDateTime } from "../../lib/format";

export function SignInSessionsCard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<SignInSessionItem[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      setSessions((await api.authSessions.list()).sessions);
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function signOutEverywhere() {
    setBusy(true);
    setError(null);
    try {
      await api.authSessions.revokeAll();
    } catch {
      setError("Could not sign you out everywhere. Please try again.");
      setBusy(false);
      return;
    }
    // Every session is ended on the server, this one included; finish here exactly as Sign out does.
    clearNavExpanded();
    try {
      await logout();
    } finally {
      navigate("/login", { replace: true });
    }
  }

  return (
    <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <h2 className="mb-1 font-heading text-lg text-ele-blue">Where you are signed in</h2>
      <p className="mb-3 text-sm text-ele-body">
        Each browser or phone you are signed in on. Office sign-ins end after 60 minutes without use and always within 12
        hours. If you see one you do not recognise, sign out everywhere.
      </p>
      <p className="mb-3 text-sm text-ele-body">
        Connected AI connections are separate from these sign-ins, and signing out everywhere does not end them. An
        administrator manages them on the Connected AI page.
      </p>

      {loadError ? (
        <p role="alert" className="text-sm text-ele-strong">
          Could not load your sign-ins. Please try again in a moment.
        </p>
      ) : sessions === null ? (
        <Loader shape="inline" />
      ) : sessions.length === 0 ? (
        <p className="text-sm text-ele-body">No sign-ins to show.</p>
      ) : (
        <ul className="divide-y divide-ele-purple-accent rounded-ele border border-ele-purple-accent">
          {sessions.map((s, i) => (
            <li key={`${s.createdAt}-${i}`} className="flex flex-wrap items-baseline justify-between gap-2 px-3 py-2 text-sm">
              <span className="text-ele-strong">
                {s.device}
                {s.current && <span className="ml-2 font-menu text-[0.65rem] uppercase tracking-wide text-ele-green">This device</span>}
              </span>
              <span className="text-xs text-ele-body">
                Signed in {formatDateTime(s.createdAt)} · Last active {formatDateTime(s.lastSeenAt)}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <ConfirmButton
          onConfirm={() => void signOutEverywhere()}
          label="Sign out everywhere"
          confirmLabel="Sign out everywhere, including this browser? Connected AI stays connected."
          busy={busy}
          busyLabel="Signing out..."
          resetMs={6000}
          className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-danger hover:text-ele-strong disabled:opacity-50"
        />
        {error && (
          <p role="alert" className="text-sm text-ele-danger">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}
